<template>
  <div class="ai-input-container">
    <div class="ai-input-wrapper">
      <el-input
        v-model="text"
        type="textarea"
        :autosize="{ minRows: 1, maxRows: 8 }"
        resize="none"
        class="ai-textarea"
        :disabled="disabled"
        :placeholder="placeholder"
        @keydown.native="onKeydown"
      />

      <div class="ai-input-actions">
        <button
          type="button"
          class="ai-round-btn"
          title="上传附件"
          @click="$emit('attach')"
        >
          <i class="el-icon-paperclip"></i>
        </button>

        <div class="ai-mode-group">
          <span
            v-for="m in modes"
            :key="m.value"
            class="ai-mode-chip"
            :class="{ 'is-active': m.value === mode }"
            @click="onSelectMode(m.value)"
            >{{ m.label }}</span
          >
        </div>

        <button
          type="button"
          class="ai-round-btn ai-send-btn"
          :class="{ 'is-ready': canSend && !sending }"
          :disabled="!canSend || sending"
          title="发送"
          @click="onSend"
        >
          <i :class="sending ? 'el-icon-loading' : 'el-icon-s-promotion'"></i>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { AI_MODES, AI_INPUT_MAX } from "../api/aiApi";

export default {
  name: "ChatInput",
  props: {
    mode: { type: String, required: true },
    sending: { type: Boolean, default: false },
    // AI 服务不可用时由父组件置 true，文本框与发送按钮禁用（附件、模式切换不受影响）
    disabled: { type: Boolean, default: false },
  },
  data() {
    return {
      text: "",
      modes: AI_MODES,
      placeholder: "输入消息提问，Enter 发送，Shift + Enter 换行",
    };
  },
  computed: {
    canSend() {
      return !this.disabled && this.text.trim().length > 0;
    },
  },
  methods: {
    onKeydown(e) {
      // 输入法组字期间回车用于确认候选，不能当作发送
      if (e.isComposing || e.keyCode === 229) return;
      // Enter 发送；Shift+Enter 换行
      if (e.key !== "Enter" || e.shiftKey) return;
      e.preventDefault();
      this.onSend();
    },
    onSelectMode(value) {
      if (value === this.mode) return;
      this.$emit("update:mode", value);
    },
    onSend() {
      if (!this.canSend || this.sending) return;
      const input = this.text.slice(0, AI_INPUT_MAX);
      if (this.text.length > AI_INPUT_MAX) {
        this.$message.warning(`消息过长，已截断为 ${AI_INPUT_MAX} 字`);
      }
      this.text = "";
      this.$emit("send", { input });
    },
  },
};
</script>

<style scoped>
.ai-input-container {
  padding: 0 20px 12px;
}

/* 圆角容器：规格取自 ai.avuejs.com/assistant 实测值 */
.ai-input-wrapper {
  background: #fff;
  border: 1px solid #e5e6eb;
  border-radius: 20px;
  box-shadow: 0 1px 6px rgba(10, 31, 61, 0.04);
  padding-bottom: 8px;
}

.ai-textarea /deep/ .el-textarea__inner {
  background: transparent;
  border: none;
  box-shadow: none;
  border-radius: 8px;
  min-height: 48px;
  padding: 16px 20px 10px;
  font-size: 14px;
  color: #4e5969;
  font-family: inherit;
}

/* 显式声明禁用态：与上面同在选择器链上压过 Element 默认样式，让禁用可见 */
.ai-textarea /deep/ .el-textarea__inner:disabled {
  color: #c9cdd4;
  cursor: not-allowed;
}

.ai-input-actions {
  display: flex;
  align-items: center;
  padding: 0 12px 0 14px;
  height: 44px;
}

.ai-round-btn {
  width: 32px;
  height: 32px;
  flex: 0 0 32px;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: #4e5969;
  font-size: 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s;
}

.ai-round-btn:hover {
  background: #f2f3f5;
}

.ai-round-btn:disabled {
  color: #c9cdd4;
  cursor: not-allowed;
  background: transparent;
}

.ai-send-btn.is-ready {
  background: #409eff;
  color: #fff;
}

.ai-send-btn.is-ready:hover {
  background: #66b1ff;
}

.ai-mode-group {
  display: flex;
  align-items: center;
  gap: 18px;
  margin-left: 18px;
  flex: 1;
}

.ai-mode-chip {
  font-size: 13px;
  color: #86909c;
  cursor: pointer;
  user-select: none;
  white-space: nowrap;
  padding: 2px 0;
  border-bottom: 2px solid transparent;
}

.ai-mode-chip:hover {
  color: #4e5969;
}

.ai-mode-chip.is-active {
  color: #409eff;
  font-weight: 600;
  border-bottom-color: #409eff;
}

/* 窄屏压缩模式胶囊占宽，避免标签折行 */
@media (max-width: 480px) {
  .ai-mode-group {
    gap: 10px;
    margin-left: 10px;
  }
  .ai-mode-chip {
    font-size: 12px;
  }
}
</style>
